if (import.meta.hot) {
  import.meta.hot.accept();
}

import './language-redirect-notice.scss';

const COOKIE_NAME = 'lang_redirect_notice';
const TRIGGER_SELECTOR = 'button[aria-controls="header-language-nav-panel"]';
const MOBILE_MENU_TRIGGER_SELECTOR = 'button[data-header-mobile-toggle]';
const LANGUAGE_PANEL_ID = 'header-language-nav-panel';
const MOBILE_MEDIA_QUERY = '(max-width: 960px)';
const SHOW_DELAY_MS = 500;
const VISIBLE_DURATION_MS = 10000;
const HIDE_TRANSITION_MS = 250;
const CONSENT_FALLBACK_MS = 3000;
const DEBUG_COOKIE_NAME = 'lang_redirect_notice_debug';
const ARROW_TIP_OFFSET_PX = 8;
const GAP_DESKTOP_PX = 14;
const GAP_MOBILE_PX = 6;

// consentmanager.net CMP: wait for the consent banner to resolve (closed, or never
// shown because consent already exists) before starting the notice's countdown,
// so the notice doesn't burn its visible time behind the consent overlay.
function whenConsentResolved(callback) {
  if (typeof window.__cmp !== 'function') {
    callback();
    return;
  }

  let resolved = false;
  let bannerOpen = false;

  const resolve = () => {
    if (resolved) return;
    resolved = true;
    callback();
  };

  window.__cmp('addEventListener', ['consentscreen', () => { bannerOpen = true; }, false], null);
  window.__cmp('addEventListener', ['consentscreenoff', resolve, false], null);
  window.__cmp('addEventListener', ['loadNotShowing', resolve, false], null);

  setTimeout(() => {
    if (!bannerOpen) resolve();
  }, CONSENT_FALLBACK_MS);
}

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? match[1] : null;
}

function deleteCookie(name) {
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

// Debug aid: run `document.cookie = 'lang_redirect_notice_debug=1; Path=/'` in the
// console to preview the notice on any reload without a real language redirect.
const isDebug = readCookie(DEBUG_COOKIE_NAME) !== null;

// On mobile the language switch trigger lives inside the closed off-canvas menu
// (offsetParent null until opened), so the notice anchors to the burger menu
// button instead — that's the only visible entry point to the language switch.
function getVisibleTrigger() {
  const languageTrigger = Array.from(document.querySelectorAll(TRIGGER_SELECTOR))
    .find((trigger) => trigger.offsetParent !== null);
  return languageTrigger ?? document.querySelector(MOBILE_MENU_TRIGGER_SELECTOR);
}

function initLanguageRedirectNotice(notice) {
  let hideTimeout = null;
  let hideAttrTimeout = null;

  const hide = () => {
    clearTimeout(hideTimeout);
    clearTimeout(hideAttrTimeout);
    // Freeze the progress bar in place before dropping is-visible — otherwise
    // it snaps back to its untransitioned scaleX(1) base state and flashes
    // fully blue for a frame while the notice fades out.
    const progressBar = notice.querySelector('.language-redirect-notice__progress-bar');
    if (progressBar) {
      progressBar.style.transform = getComputedStyle(progressBar).transform;
    }
    notice.classList.remove('is-visible');
    hideAttrTimeout = setTimeout(() => {
      notice.hidden = true;
      if (progressBar) progressBar.style.transform = '';
    }, HIDE_TRANSITION_MS);
  };

  const position = (trigger) => {
    // The desktop trigger renders its label before the globe icon, so the
    // button's own center sits left of the icon — anchor to the icon itself.
    const anchor = trigger.querySelector('.header__meta-icon') ?? trigger;
    const anchorRect = anchor.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const gap = window.matchMedia(MOBILE_MEDIA_QUERY).matches ? GAP_MOBILE_PX : GAP_DESKTOP_PX;
    notice.style.top = `${triggerRect.bottom + window.scrollY + gap + ARROW_TIP_OFFSET_PX}px`;
    notice.style.left = `${anchorRect.left + window.scrollX + anchorRect.width / 2}px`;
  };

  const show = () => {
    const trigger = getVisibleTrigger();
    if (!trigger) return;
    position(trigger);
    notice.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => notice.classList.add('is-visible')));
    if (!isDebug) {
      hideTimeout = setTimeout(hide, VISIBLE_DURATION_MS);
    }
  };

  window.addEventListener('resize', () => {
    if (notice.hidden) return;
    const trigger = getVisibleTrigger();
    if (trigger) {
      position(trigger);
    } else {
      hide();
    }
  });

  document.addEventListener('sidebar:beforeopen', (event) => {
    if (event.detail?.id === LANGUAGE_PANEL_ID) hide();
  });

  const link = notice.querySelector('[data-language-redirect-notice-link]');
  const languageTriggerAriaLabel = document.querySelector(TRIGGER_SELECTOR)?.getAttribute('aria-label');
  if (link && languageTriggerAriaLabel) {
    link.setAttribute('aria-label', languageTriggerAriaLabel);
  }
  link?.addEventListener('click', () => {
    window.altanaSidebar?.getById(LANGUAGE_PANEL_ID)?.open(getVisibleTrigger());
  });

  notice.style.setProperty('--language-redirect-notice-duration', `${VISIBLE_DURATION_MS}ms`);

  whenConsentResolved(() => setTimeout(show, SHOW_DELAY_MS));
}

document.querySelectorAll('[data-language-redirect-notice]').forEach((notice) => {
  if (!isDebug) {
    if (readCookie(COOKIE_NAME) === null) return;
    deleteCookie(COOKIE_NAME);
  }
  initLanguageRedirectNotice(notice);
});
